
import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface BlockDatesTabProps {
  propertyId: string;
  existingBookings?: {
    check_in_date: string;
    check_out_date: string;
    status: string;
  }[];
}

export const BlockDatesTab = ({ propertyId, existingBookings = [] }: BlockDatesTabProps) => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleDateSelect = (dates: Date[] | undefined) => {
    if (!dates) return;
    setSelectedDates(dates);
  };

  const isDateBooked = (date: Date) => {
    return existingBookings.some(booking => {
      const checkIn = new Date(booking.check_in_date);
      const checkOut = new Date(booking.check_out_date);
      return date >= checkIn && date <= checkOut;
    });
  };

  const handleBlockDates = async () => {
    if (!selectedDates.length) return;

    setIsLoading(true);
    try {
      // First, get existing blocked dates
      const { data: existingData, error: fetchError } = await supabase
        .from('calendar_syncs')
        .select('blocked_dates')
        .eq('property_id', propertyId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      const formattedDates = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      
      if (existingData) {
        // Update existing record
        const { error } = await supabase
          .from('calendar_syncs')
          .update({
            blocked_dates: [...(existingData.blocked_dates || []), ...formattedDates]
          })
          .eq('property_id', propertyId);

        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('calendar_syncs')
          .insert({
            property_id: propertyId,
            blocked_dates: formattedDates,
            platform_name: 'manual',
            sync_url: 'manual'
          });

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "Calendar has been updated successfully",
      });
      setSelectedDates([]);
    } catch (error) {
      console.error('Error updating calendar:', error);
      toast({
        title: "Error",
        description: "Failed to update calendar. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-4 max-w-full overflow-hidden">
      <div className="flex justify-center">
        <div className="w-full max-w-[400px] mx-auto overflow-hidden">
          <Calendar
            mode="multiple"
            selected={selectedDates}
            onSelect={handleDateSelect}
            disabled={(date) => isDateBooked(date) || date < new Date()}
            className="rounded-md border w-full bg-white"
            classNames={{
              day_selected: "bg-primary text-primary-foreground hover:bg-primary/90",
              day_today: "bg-accent text-accent-foreground",
              day_disabled: "text-muted-foreground opacity-50",
              day_range_middle: "bg-accent/50",
              day_hidden: "invisible",
              day: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-accent/50 rounded-full transition-colors",
              head_cell: "text-muted-foreground rounded-md w-10 font-normal text-[0.8rem]",
              cell: "h-10 w-10 text-center text-sm relative p-0 focus-within:relative focus-within:z-20",
              month: "space-y-4 w-full",
              caption: "flex justify-center pt-1 relative items-center mb-4",
              nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute",
              nav_button_previous: "left-1",
              nav_button_next: "right-1",
              table: "w-full border-collapse space-y-1",
              tbody: "w-full"
            }}
          />
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3 justify-center mt-6">
        <Badge variant="outline" className="gap-1">
          <div className="h-2 w-2 rounded-full bg-primary" />
          Blocked
        </Badge>
        <Badge variant="outline" className="gap-1">
          <div className="h-2 w-2 rounded-full bg-red-500" />
          Booked
        </Badge>
        <Badge variant="outline" className="gap-1">
          <div className="h-2 w-2 rounded-full bg-accent" />
          Today
        </Badge>
      </div>

      {selectedDates.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {selectedDates.map((date) => (
              <Badge
                key={date.toISOString()}
                variant="secondary"
                className="flex items-center gap-1 py-1.5"
              >
                {format(date, 'MMM dd, yyyy')}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={() => setSelectedDates(prev => prev.filter(d => d !== date))}
                />
              </Badge>
            ))}
          </div>

          <Button
            onClick={handleBlockDates}
            disabled={isLoading || !selectedDates.length}
            className="w-full max-w-md mx-auto"
          >
            {isLoading ? "Updating..." : "Block Selected Dates"}
          </Button>
        </div>
      )}
    </div>
  );
};
